import type { SmbAdminDetail, SmbAdminStep } from "@shared/smb-admin";
import type { SmbBoardCard } from "@shared/smb-board";
import { SMB_ENRICHMENT_STEPS } from "@shared/smb-enrichment";

export const PHASES = ["Intake", "Package and templates", "Build", "Review", "Deliver"] as const;
export type PhaseStatus = "not started" | "in progress" | "needs you" | "done";
export type PhaseAction = { key: string; label: string; paid: boolean; disabled: boolean; reason?: string };
const payload = (step?: SmbAdminStep) => step?.description ?? step?.state?.result ?? {};
export const paidStep = (key: string) => ["enrichment", "research", "services", "personas", "generation", "handoff.start_run"].includes(key);

// A completed registry invocation can mean just one enrichment unit ran.
// Inspection and authoritative order status take precedence over invocation flags.
export function resolveSmbPhases(detail: SmbAdminDetail): {
  current: number; phases: Array<{ label: typeof PHASES[number]; status: PhaseStatus }>;
  action: PhaseAction | null; held: boolean; enriched: boolean; attached: boolean; generated: boolean; delivered: boolean;
} {
  const step = (key: string) => detail.steps.find(item => item.key === key);
  const intake = payload(step("intake")), templates = payload(step("templates"));
  const enrichment = payload(step("enrichment")), handoff = payload(step("handoff"));
  const generation = payload(step("generation")), review = payload(step("review")), publish = payload(step("publish"));
  const personaJobCounts = enrichment.personaJobCounts && typeof enrichment.personaJobCounts === "object"
    ? enrichment.personaJobCounts as Record<string, unknown> : {};
  const personaCount = (key: string) => typeof personaJobCounts[key] === "number" && Number.isFinite(personaJobCounts[key])
    ? Math.max(0, personaJobCounts[key] as number) : 0;
  const personaWaiting = personaCount("waiting"), personaRunning = personaCount("running");
  const personaRecoveryAvailable = enrichment.personaRecoveryAvailable === true;
  const personaRecoveryBlocked = enrichment.personaRecoveryBlocked === true;
  const personaBatchRunning = personaRunning > 0 || enrichment.personaBatchStatus === "running";
  const reviewFlags = Array.isArray(enrichment.reviewFlags) ? enrichment.reviewFlags : [];
  const status = detail.order.status;
  const postBuild = ["qa", "ready", "published"].includes(status);
  const buildStarted = postBuild || status === "generating";
  // Package selection freezes its snapshot at order creation. The templates
  // registry step is optional inspection, with no live describe hook; its run
  // history must not become a prerequisite for reaching the real current phase.
  const configured = !!detail.order.packageId || !!detail.package ||
    templates.configured === true || !!templates.manifest || buildStarted ||
    (typeof handoff.runId === "string" && !!handoff.runId);
  // Later authoritative lifecycle states prove these prerequisites were passed,
  // even when old inspection/enrichment history is missing. They never replace
  // per-item human review or actual published-delivery evidence.
  const accepted = intake.accepted === true || buildStarted;
  const enriched = enrichment.completed === true || buildStarted;
  const attached = (typeof handoff.runId === "string" && !!handoff.runId) || buildStarted;
  const generated = postBuild || (generation.status === "completed" && generation.pending === 0 && attached);
  const reviewed = ["ready", "published"].includes(status);
  const delivered = publish.completed === true;
  const done = [accepted, configured, enriched && attached && generated, reviewed, delivered];
  const terminal = ["failed", "cancelled"].includes(status);
  const running = detail.steps.some(item => item.state?.status === "running") || status === "generating";
  const held = enrichment.reviewRequired === true;
  const personaFailureOnlyHold = held && reviewFlags.length > 0 &&
    reviewFlags.every(flag => flag === "persona_failures");
  const otherPersonaHold = reviewFlags.some(flag => flag !== "persona_failures") ||
    (held && !personaFailureOnlyHold);
  let current = done.findIndex(value => !value);
  if (current === -1) current = 4;
  // Missing history alone is not completion; package/lifecycle evidence above is.
  let action: PhaseAction | null = null;
  function candidate(key: string, label: string, allowed = true, reason?: string) {
    const parent = SMB_ENRICHMENT_STEPS.includes(key as typeof SMB_ENRICHMENT_STEPS[number]) ? "enrichment" : key;
    const registered = step(parent)?.registered === true;
    action = { key, label, paid: paidStep(key), disabled: terminal || running || !registered || !allowed,
      reason: terminal ? "This order is closed. Inspect recovery options in Details." : running ? "Work is running. Progress refreshes automatically." :
        !registered ? "This action is not registered." : !allowed ? reason ?? "Review the current requirements first." : undefined };
  }
  if (current === 0) candidate("intake", "Accept intake", false, "Submit client answers using the Intake card in Details.");
  if (current === 1) candidate("templates", "Check templates", !!detail.package, "No package is attached. Package changes are not available after creation.");
  if (current === 2) {
    if (!enriched) {
      const next = typeof enrichment.nextStep === "string" && (SMB_ENRICHMENT_STEPS as readonly string[]).includes(enrichment.nextStep) ? enrichment.nextStep : null;
      const labels: Record<string, string> = { provision: "Prepare brand", apply: "Apply client answers", research: "Run research", services: "Prepare services", personas: "Build personas", confidence: "Check build readiness" };
      if (typeof enrichment.completedCount === "number" && enrichment.completedCount >= 10) {
        labels.personas = enrichment.selectedCount === 3
          ? enrichment.narrativeAngleStatus === "in_progress" ? "Resume narrative angles" : "Build narrative angles"
          : "Select 3 personas and build narrative angles";
      }
      if (next === "personas" && personaRecoveryBlocked) {
        candidate("personas", "Inspect interrupted personas", false, "This batch needs manual inspection. Open Details for recovery information.");
      } else if (next === "personas" && personaRecoveryAvailable && (!personaBatchRunning || enrichment.personaRecoveryKind === "saved_results")) {
        candidate("personas", enrichment.personaRecoveryKind === "saved_results" ? "Fix saved persona results" : "Resume interrupted personas", !otherPersonaHold,
          otherPersonaHold ? "Resolve the other review hold requirements before resuming." : undefined);
      } else if (next === "personas" && (personaBatchRunning || personaWaiting > 0)) {
        candidate("personas", "Persona work is in progress", false, "Persona jobs are waiting or running. Refresh status before taking another action.");
      } else {
        candidate(next ?? "enrichment", next ? labels[next] : "Run build", !!next && !held, held ? "Review the research hold below before continuing." : "Build progress is unavailable. Refresh or inspect Details.");
      }
    } else if (!attached) candidate("handoff", "Prepare records");
    else candidate("generation", "Generate assets", !generated, "Generation has finished. Review its results.");
  }
  if (current === 3) candidate("review", "Complete review", review.completed === true, "Approve or drop the individual assets and resolve readiness requirements below.");
  if (current === 4 && !delivered) candidate("publish", "Publish to client", publish.canPublish === true, "Only ready, fully reviewed orders can publish. Inspect delivery status below.");
  const phases = PHASES.map((label, index) => ({
    label, status: (done[index] ? "done" : index > current ? "not started" : index < current ? "needs you" :
      running ? "in progress" : "needs you") as PhaseStatus,
  }));
  return { current, phases, action, held, enriched, attached, generated, delivered };
}

export function clientProgress(order: Pick<SmbBoardCard, "status" | "nextAction">) {
  const labels: Record<string, string> = {
    awaiting_intake: "Waiting for intake", intake_received: "Intake received", enriching: "Preparing the client",
    needs_review: "Needs staff review", generating: "Building assets", qa: "Reviewing results", ready: "Ready to publish",
    published: "Published", failed: "Needs attention", cancelled: "Cancelled",
  };
  return labels[order.status] ?? (order.nextAction.available ? order.nextAction.label : "Inspect client");
}
